import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock, Mail, Phone, User as UserIcon, X } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { useModalMotion } from '@/hooks/useModalMotion';
import { authService } from '@/features/auth/services/authService';
import { notificationService } from '@/services/notificationService';
import { playClickSound } from '@/utils/soundUtils';
import { FORGOT_PASSWORD_LOGIN_PATH } from '@/features/auth/constants/authRoutes';
import { getPendingCheckout } from '@/features/finance/utils/pendingCheckout';
import { CountrySelectWithSearch } from '@/features/auth/components/CountrySelectWithSearch';
import './PhoneInputAuth.css';

type AuthTab = 'username' | 'phone' | 'email';

const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

/** V1 login panel: navy glass + cyan accent */
const loginPanelStyle: CSSProperties = {
  border: '1px solid rgba(56, 189, 248, 0.22)',
  background: 'linear-gradient(180deg, rgba(8, 15, 35, 0.96) 0%, rgba(5, 10, 27, 0.94) 100%)',
  boxShadow: '0 0 0 1px rgba(168, 85, 247, 0.06), 0 12px 40px rgba(0, 0, 0, 0.45)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

const tabActiveStyle: CSSProperties = {
  background: 'linear-gradient(90deg, #f857a6 0%, #a855f7 100%)',
  boxShadow: '0 0 18px rgba(168, 85, 247, 0.45)',
  borderRadius: 10,
  border: '1px solid transparent',
};

const tabInactiveStyle: CSSProperties = {
  background: '#0c1222',
  border: '1px solid #1e2645',
  borderRadius: 10,
};

const primaryCtaStyle: CSSProperties = {
  background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
  boxShadow: '0 4px 20px rgba(124, 58, 237, 0.35)',
};

const getCodeActiveStyle: CSSProperties = {
  background: 'linear-gradient(180deg, #FFED4A 0%, #FFCC00 100%)',
  boxShadow: '0 0 16px #FFCC0066',
  color: '#000',
};

const fieldShellClass =
  'relative w-full flex items-center rounded-lg border border-white/10 bg-white/10 backdrop-blur-md min-h-[40px] sm:min-h-[44px] overflow-hidden';

/**
 * Auth modal styled after V1 login. Sign in: Username | Phone | Email.
 * Register: Phone | Email (OTP). Username tab supports 2FA (email OTP) when enabled.
 */
export default function AuthModalHost() {
  const { isOpen, mode, closeAuthModal, setAuthMode, notifyAuthenticated } = useAuthModal();
  const { render, backdropClass, panelClass } = useModalMotion(isOpen);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tab, setTab] = useState<AuthTab>('username');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Username+password tab: credentials OK and waiting for email OTP (2FA). */
  const [username2FA, setUsername2FA] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState('');

  const isRegister = mode === 'register';
  const orderSaved = !!getPendingCheckout();
  const otpTab = isRegister || tab !== 'username' || username2FA;

  useEffect(() => {
    if (!isOpen) {
      setShowPassword(false);
      setUsername2FA(false);
      setTwoFactorEmail('');
      setTwoFactorMaskedEmail('');
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuthModal();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeAuthModal]);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [otpCooldown]);

  useEffect(() => {
    if (isRegister && tab === 'username') setTab('phone');
  }, [isRegister, tab]);

  if (!render) return null;

  const resetErrors = () => setError(null);

  const resetUsername2FA = () => {
    setUsername2FA(false);
    setTwoFactorEmail('');
    setTwoFactorMaskedEmail('');
  };

  const reportError = (msg: string) => {
    setError(msg);
    notificationService.error(msg);
  };

  const completeLogin = async (opts?: { isRegister?: boolean }) => {
    if (opts?.isRegister) {
      notificationService.success(t('auth.registerSuccess', 'Account created — you are signed in'));
    } else {
      notificationService.success(t('auth.loginSuccessRedirecting', 'Login successful!'), { duration: 2000 });
    }

    try {
      const me = await authService.getCurrentUser();
      const meUser = (me.data as { user?: { profile?: { mustChangePassword?: boolean } } })?.user;
      if (meUser?.profile?.mustChangePassword) {
        notificationService.info(t('auth.mustChangePasswordNotice'), { duration: 2500 });
        closeAuthModal();
        navigate('/account/security', { replace: true });
        return;
      }
    } catch {
      // Token already stored by authService; continue.
    }

    notifyAuthenticated();
  };

  const handleTab = (next: AuthTab) => {
    playClickSound();
    setTab(next);
    setOtp('');
    setOtpCooldown(0);
    resetUsername2FA();
    resetErrors();
  };

  const sendUsername2FACode = async (target: string) => {
    if (sendingCode || otpCooldown > 0) return false;
    const id = target.trim();
    if (!id) {
      reportError(
        t(
          'auth.twoFactorEmailUnavailable',
          'Could not send a code to your registered email. Please try again or contact support.',
        ),
      );
      return false;
    }
    setSendingCode(true);
    try {
      const result = await authService.sendLoginCode(id);
      if (!result.success) {
        reportError(result.message || t('auth.failedToSendCode', 'Failed to send code'));
        if (result.cooldownSeconds) setOtpCooldown(result.cooldownSeconds);
        return false;
      }
      const resolvedEmail = (result.email || (authService.validateEmail(id).isValid ? id : twoFactorEmail) || '').trim();
      const masked =
        result.maskedEmail ||
        (resolvedEmail ? authService.maskEmail(resolvedEmail) : twoFactorMaskedEmail) ||
        '';
      if (resolvedEmail) setTwoFactorEmail(resolvedEmail);
      if (masked) setTwoFactorMaskedEmail(masked);
      setOtpCooldown(result.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
      notificationService.success(
        masked
          ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
          : t('auth.verificationSentEmail', 'Verification code sent to your email'),
      );
      return true;
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        t('auth.failedToSendVerification', 'Failed to send verification code. Please try again.');
      reportError(msg);
      return false;
    } finally {
      setSendingCode(false);
    }
  };

  const enterUsername2FA = async (opts: {
    email?: string;
    maskedEmail?: string;
    codeSent?: boolean;
    cooldownSeconds?: number;
  }) => {
    const emailForCode = (opts.email || '').trim();
    const masked = opts.maskedEmail || (emailForCode ? authService.maskEmail(emailForCode) : '');
    setUsername2FA(true);
    setTwoFactorEmail(emailForCode);
    setTwoFactorMaskedEmail(masked);
    setOtp('');
    setOtpCooldown(0);
    resetErrors();

    // Backend already emailed OTP on the credential challenge.
    if (opts.codeSent) {
      setOtpCooldown(opts.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
      notificationService.success(
        masked
          ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
          : t('auth.verificationSentEmail', 'Verification code sent to your email'),
      );
      return;
    }

    // Prefer registered email from API; otherwise resend via login username/identifier.
    const sendTarget = emailForCode || username.trim();
    await sendUsername2FACode(sendTarget);
  };

  const handleSendCode = async () => {
    if (sendingCode || otpCooldown > 0 || busy) return;
    resetErrors();
    playClickSound();

    if (username2FA) {
      await sendUsername2FACode(twoFactorEmail.trim() || username.trim());
      return;
    }

    setSendingCode(true);
    try {
      if (tab === 'email') {
        const emailVal = email.trim();
        if (!emailVal) {
          setError(t('auth.emailRequired', 'Email address is required'));
          return;
        }
        const emailCheck = authService.validateEmail(emailVal);
        if (!emailCheck.isValid) {
          setError(emailCheck.message);
          return;
        }
        const result = await authService.sendEmailOTP(emailVal);
        if (!result.success) {
          reportError(result.message || t('auth.failedToSendCode', 'Failed to send code'));
          if (result.cooldownSeconds) setOtpCooldown(result.cooldownSeconds);
          return;
        }
        setOtpCooldown(result.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
        notificationService.success(t('auth.verificationSentEmail', 'Verification code sent to your email'));
        return;
      }

      const phoneVal = phone.trim();
      if (!phoneVal) {
        setError(t('auth.phoneRequired', 'Phone number is required'));
        return;
      }
      const phoneCheck = authService.validatePhone(phoneVal);
      if (!phoneCheck.isValid) {
        setError(phoneCheck.message);
        return;
      }
      const result = await authService.sendPhoneLoginCode(phoneVal);
      if (!result.success) {
        reportError(result.message || t('auth.failedToSendCode', 'Failed to send code'));
        if (result.cooldownSeconds) setOtpCooldown(result.cooldownSeconds);
        return;
      }
      setOtpCooldown(result.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
      notificationService.success(t('auth.verificationSentPhone', 'Verification code sent to your phone'));
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        t('auth.failedToSendVerification', 'Failed to send verification code. Please try again.');
      reportError(msg);
    } finally {
      setSendingCode(false);
    }
  };

  const toggleMode = () => {
    playClickSound();
    resetErrors();
    setOtp('');
    setOtpCooldown(0);
    resetUsername2FA();
    if (!isRegister && tab === 'username') setTab('phone');
    setAuthMode(isRegister ? 'signin' : 'register');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    resetErrors();
    playClickSound();
    setBusy(true);
    try {
      if (!isRegister && tab === 'username') {
        if (username2FA) {
          const code = otp.trim();
          if (!code) {
            reportError(t('auth.verificationCodeRequired', 'Verification code is required'));
            return;
          }
          if (code.length !== 6) {
            reportError(t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits'));
            return;
          }
          const result = await authService.authenticateUser(
            {
              username: username.trim(),
              email: twoFactorEmail || '',
              password,
              emailCode: code,
              phone: '',
              phoneCode: '',
            },
            true,
          );
          if (!result.success) {
            reportError(result.message || t('auth.loginFailed', 'Sign in failed'));
            return;
          }
          await completeLogin();
          return;
        }

        if (!username.trim() || !password) {
          setError(t('auth.usernamePasswordRequired', 'Username and password are required'));
          return;
        }

        const probe = await authService.validateCredentialsWithAPI(username.trim(), password);
        if (!probe.success) {
          reportError(probe.message || t('auth.loginFailed', 'Sign in failed'));
          if (probe.cooldownSeconds) setOtpCooldown(probe.cooldownSeconds);
          return;
        }

        if (!probe.two_factor_enabled) {
          // Token already stored by validate (email) or login probe (username).
          await completeLogin();
          return;
        }

        await enterUsername2FA({
          email: probe.email,
          maskedEmail: probe.maskedEmail,
          codeSent: probe.codeSent,
          cooldownSeconds: probe.cooldownSeconds,
        });
        return;
      }

      if (tab === 'email') {
        if (!email.trim()) {
          setError(t('auth.emailRequired', 'Email address is required'));
          return;
        }
        if (!otp.trim()) {
          setError(t('auth.verificationCodeRequired', 'Verification code is required'));
          return;
        }
        if (otp.trim().length !== 6) {
          setError(t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits'));
          return;
        }
        const result = await authService.loginWithEmailCode(email.trim(), otp.trim());
        if (!result.success) {
          reportError(result.message || t('auth.loginFailed', 'Sign in failed'));
          return;
        }
        await completeLogin({ isRegister });
        return;
      }

      if (!phone.trim()) {
        setError(t('auth.phoneRequired', 'Phone number is required'));
        return;
      }
      if (!otp.trim()) {
        setError(t('auth.verificationCodeRequired', 'Verification code is required'));
        return;
      }
      if (otp.trim().length !== 6) {
        setError(t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits'));
        return;
      }
      const result = await authService.loginWithPhoneCode(phone.trim(), otp.trim());
      if (!result.success) {
        reportError(result.message || t('auth.loginFailed', 'Sign in failed'));
        return;
      }
      await completeLogin({ isRegister });
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || t('auth.loginFailedTryAgain', 'Something went wrong');
      reportError(msg);
    } finally {
      setBusy(false);
    }
  };

  const visibleTabs: readonly [AuthTab, string, typeof UserIcon][] = isRegister
    ? [
        ['phone', 'Phone', Phone],
        ['email', 'Email', Mail],
      ]
    : [
        ['username', 'Username', UserIcon],
        ['phone', 'Phone', Phone],
        ['email', 'Email', Mail],
      ];

  const field1Label =
    tab === 'username'
      ? t('auth.username', 'Username')
      : tab === 'phone'
        ? t('auth.phone', 'Phone number')
        : t('auth.email', 'Email address');

  const contactReady = tab === 'email' ? !!email.trim() : tab === 'phone' ? !!phone.trim() : false;
  const canSendCode =
    ((otpTab && !username2FA && contactReady) ||
      (username2FA && !!(twoFactorEmail.trim() || username.trim()))) &&
    !sendingCode &&
    otpCooldown <= 0 &&
    !busy;
  const canSubmit = otpTab ? otp.trim().length === 6 : !!username.trim() && !!password;

  const ctaLabel = isRegister
    ? t('auth.createAccount', 'Create account')
    : username2FA
      ? t('auth.verifyAndSignIn', 'Verify & sign in')
      : t('header.signIn', 'Sign in');

  const title = isRegister
    ? t('header.register', 'Register')
    : username2FA
      ? t('auth.twoFactorTitle', 'Two-factor authentication')
      : t('header.signIn', 'Sign in');

  const sectionLabel =
    username2FA
      ? null
      : tab === 'phone'
        ? t('auth.phoneLogin', 'Phone Login')
        : tab === 'email'
          ? t('auth.emailLogin', 'Email Login')
          : null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[var(--wf-z-modal-nested)] flex items-center justify-center cosmos-modal-backdrop ${backdropClass}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-mdl-title"
    >
      <form
        className={`auth-mdl-v1 relative ${panelClass} flex flex-col w-full max-h-[min(92vh,720px)] overflow-y-auto`}
        style={{
          width: 'min(var(--wf-mdl-w), 100%)',
          maxWidth: 'min(32rem, var(--wf-mdl-viewport-max))',
          padding: '1rem 1.1rem 1.15rem',
          gap: '0.75rem',
          borderRadius: '1rem',
          colorScheme: 'dark',
          ...loginPanelStyle,
        }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between gap-2">
          <h2
            id="auth-mdl-title"
            className="min-w-0 text-base sm:text-lg font-bold text-white tracking-wide"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={() => {
              playClickSound();
              closeAuthModal();
            }}
            aria-label="Close"
            className="text-white/55 hover:text-white transition-colors shrink-0 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {orderSaved && (
          <div className="inline-block self-start px-3 py-1.5 bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md rounded-lg border border-green-400/30">
            <p className="text-green-200 text-xs font-semibold">
              {t('auth.orderSaved', 'Your order is saved ✓')}
            </p>
          </div>
        )}

        {!username2FA && (
          <div
            className={`grid gap-1.5 sm:gap-2 w-full ${isRegister ? 'grid-cols-2' : 'grid-cols-3'}`}
          >
            {visibleTabs.map(([id, label, Icon]) => {
              const active = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleTab(id)}
                  className={`py-2 sm:py-2.5 px-2 sm:px-3 font-semibold text-xs sm:text-sm transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    active ? 'text-white' : 'text-white/65 hover:text-white/90'
                  }`}
                  style={active ? tabActiveStyle : tabInactiveStyle}
                >
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{t(`auth.tab.${id}`, label)}</span>
                </button>
              );
            })}
          </div>
        )}

        {isRegister && !username2FA && (
          <p className="text-xs text-white/70 leading-relaxed px-0.5">
            {t(
              'auth.registerPhoneOrEmailOnly',
              'New accounts can only be created with phone or email — username sign-in is for existing accounts.',
            )}
          </p>
        )}

        {username2FA ? (
          <p className="text-xs text-white/75 leading-snug">
            {twoFactorMaskedEmail
              ? t('auth.twoFactorHint', 'Enter the 6-digit code sent to {{email}}', {
                  email: twoFactorMaskedEmail,
                })
              : t(
                  'auth.twoFactorHintGeneric',
                  'Enter the 6-digit code sent to your registered email',
                )}
          </p>
        ) : (
          <div className="flex flex-col gap-2.5 sm:gap-3 w-full">
            {sectionLabel && (
              <div className="flex items-center gap-2 w-full">
                {tab === 'phone' ? (
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                ) : (
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                )}
                <span className="text-white font-semibold text-xs sm:text-sm">{sectionLabel}</span>
              </div>
            )}

            <div className="flex flex-col gap-1.5 w-full">
              <label className="block text-xs font-medium text-white">
                {field1Label}
              </label>
              {tab === 'phone' ? (
                <div className={`${fieldShellClass} overflow-visible px-2.5 sm:px-3`}>
                  <PhoneInput
                    international={false}
                    defaultCountry="US"
                    countryOptionsOrder={['US', 'CA', 'GB', 'VN', '…']}
                    placeholder={t('auth.phoneIntlPlaceholder', 'Phone number')}
                    value={phone || undefined}
                    onChange={(v) => setPhone(v ?? '')}
                    countrySelectComponent={CountrySelectWithSearch}
                    className="PhoneInputAuth w-full"
                    numberInputProps={{ 'aria-label': field1Label }}
                  />
                </div>
              ) : (
                <div className={fieldShellClass}>
                  <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                    {tab === 'email' ? (
                      <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" aria-hidden />
                    ) : (
                      <UserIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" aria-hidden />
                    )}
                  </div>
                  <input
                    className="auth-mdl-field__input block w-full min-h-[40px] sm:min-h-[44px] pl-7 sm:pl-9 pr-2.5 py-0 border-none bg-transparent text-white placeholder-gray-400 focus:outline-none text-xs sm:text-sm"
                    placeholder={field1Label}
                    type={tab === 'email' ? 'email' : 'text'}
                    autoComplete={tab === 'username' ? 'username' : 'email'}
                    value={tab === 'username' ? username : email}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (tab === 'username') setUsername(v);
                      else setEmail(v);
                    }}
                  />
                </div>
              )}
              {tab === 'phone' && (
                <p className="text-xs text-white/60">
                  {t('auth.phoneCountryHint', 'Select country and enter your phone number')}
                </p>
              )}
            </div>
          </div>
        )}

        {otpTab ? (
          <div className="flex flex-col gap-1.5 w-full">
            {!username2FA && (
              <label className="block text-xs font-medium text-white">
                {t('auth.verificationCode', 'Verification Code')}
              </label>
            )}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 items-center w-full">
              <div className={`${fieldShellClass} flex-1 min-w-0`}>
                <input
                  className="auth-mdl-field__input block w-full min-h-[40px] sm:min-h-[44px] px-2.5 py-0 border-none bg-transparent text-white placeholder-gray-400 focus:outline-none text-xs sm:text-sm text-center font-mono tracking-widest"
                  placeholder="000000"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
              <button
                type="button"
                disabled={!canSendCode}
                onClick={handleSendCode}
                className={`shrink-0 px-2.5 sm:px-3 py-2 rounded-lg font-semibold text-xs transition-all min-w-[80px] sm:min-w-[90px] ${
                  !canSendCode
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'active:scale-95'
                }`}
                style={!canSendCode ? undefined : getCodeActiveStyle}
              >
                {sendingCode
                  ? '…'
                  : otpCooldown > 0
                    ? `${otpCooldown}s`
                    : username2FA
                      ? t('auth.resendCode', 'Resend')
                      : t('auth.getCode', 'GET CODE')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5 w-full">
            <label className="block text-xs font-medium text-white">
              {t('auth.password', 'Password')}
            </label>
            <div className={fieldShellClass}>
              <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                <Lock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" aria-hidden />
              </div>
              <input
                className="auth-mdl-field__input block w-full min-h-[40px] sm:min-h-[44px] pl-7 sm:pl-9 pr-10 sm:pr-11 py-0 border-none bg-transparent text-white placeholder-gray-400 focus:outline-none text-xs sm:text-sm"
                placeholder={t('auth.password', 'Password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => {
                  playClickSound();
                  setShowPassword((v) => !v);
                }}
                className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-white/90 p-0.5"
                aria-label={
                  showPassword
                    ? t('auth.hidePassword', 'Hide password')
                    : t('auth.showPassword', 'Show password')
                }
              >
                {showPassword ? (
                  <EyeOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden />
                ) : (
                  <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden />
                )}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="p-2 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-400 text-xs whitespace-pre-wrap">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !canSubmit}
          className={`w-full rounded-lg py-2.5 text-white font-bold text-xs sm:text-sm transition-opacity ${
            busy || !canSubmit ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-95 active:scale-[0.99]'
          }`}
          style={primaryCtaStyle}
        >
          {busy ? t('common.loading', '…') : ctaLabel}
        </button>

        <div
          className={`flex items-center gap-2 text-xs ${
            username2FA || (!isRegister && tab === 'username') || isRegister
              ? 'justify-between'
              : 'justify-end'
          }`}
        >
          {username2FA ? (
            <button
              type="button"
              className="text-fuchsia-300/90 hover:text-fuchsia-200 underline-offset-2 hover:underline"
              onClick={() => {
                playClickSound();
                resetUsername2FA();
                setOtp('');
                setOtpCooldown(0);
                resetErrors();
              }}
            >
              {t('auth.backToPassword', 'Back')}
            </button>
          ) : (
            !isRegister &&
            tab === 'username' && (
              <button
                type="button"
                className="text-fuchsia-300/90 hover:text-fuchsia-200 underline-offset-2 hover:underline"
                onClick={() => {
                  playClickSound();
                  closeAuthModal();
                  navigate(FORGOT_PASSWORD_LOGIN_PATH);
                }}
              >
                {t('auth.forgotPassword', 'Forgot password?')}
              </button>
            )
          )}
          {!username2FA && isRegister && (
            <button
              type="button"
              className="text-sky-300 hover:text-sky-200 underline-offset-2 hover:underline text-right"
              onClick={toggleMode}
            >
              {t('auth.haveAccount', 'Have an account? Sign in')}
            </button>
          )}
        </div>

        {!isRegister && !username2FA && tab === 'username' && (
          <p className="text-center text-xs text-white/70 px-1 leading-relaxed w-full">
            {t('auth.noAccountHintPrefix', 'No account? You can also login via')}{' '}
            <button
              type="button"
              onClick={() => handleTab('phone')}
              className="underline underline-offset-2 text-sky-300 hover:text-sky-200 p-0 border-0 bg-transparent font-inherit cursor-pointer"
            >
              {t('auth.tab.phone', 'phone number')}
            </button>{' '}
            {t('common.or', 'or')}{' '}
            <button
              type="button"
              onClick={() => handleTab('email')}
              className="underline underline-offset-2 text-sky-300 hover:text-sky-200 p-0 border-0 bg-transparent font-inherit cursor-pointer"
            >
              {t('auth.tab.email', 'email')}
            </button>
            .
          </p>
        )}
      </form>
    </div>,
    document.body,
  );
}
